// ---------------------------------------------------------------------------
// C5-C7: Density Mode Provider
// ---------------------------------------------------------------------------
// Three modes: easy (spacious) / normal (balanced) / power (dense).
// data-density attribute on root. Per-module overrides.
// Persists to localStorage + syncs to user prefs API.
// ---------------------------------------------------------------------------
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Density = "easy" | "normal" | "power";

type ModuleName =
  | "crm"
  | "email"
  | "pages"
  | "workflows"
  | "inbox"
  | "ai"
  | "analytics"
  | "services"
  | "webinars"
  | "communications"
  | "reputation"
  | "marketplace"
  | "admin";

interface DensityContextValue {
  /** Global density setting */
  global: Density;
  /** Per-module overrides */
  moduleOverrides: Partial<Record<ModuleName, Density>>;
  /** Resolved density for current route module */
  effective: Density;
  /** Set global density */
  setGlobal: (d: Density) => void;
  /** Set per-module override */
  setModuleOverride: (module: ModuleName, d: Density | null) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY_GLOBAL = "ease:density:global";
const STORAGE_KEY_OVERRIDES = "ease:density:overrides";
const DEFAULT_DENSITY: Density = "normal";

// ---------------------------------------------------------------------------
// Module detection from route
// ---------------------------------------------------------------------------

function detectModule(pathname: string): ModuleName | null {
  const segment = pathname.split("/").filter(Boolean)[0];
  if (!segment) return null;

  const MODULE_MAP: Record<string, ModuleName> = {
    crm: "crm",
    email: "email",
    pages: "pages",
    workflows: "workflows",
    inbox: "inbox",
    ai: "ai",
    analytics: "analytics",
    services: "services",
    webinars: "webinars",
    communications: "communications",
    reputation: "reputation",
    marketplace: "marketplace",
    admin: "admin",
  };

  return MODULE_MAP[segment] ?? null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const DensityContext = createContext<DensityContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function DensityProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  // Load from localStorage
  const [global, setGlobalState] = useState<Density>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_GLOBAL);
      if (stored === "easy" || stored === "normal" || stored === "power") return stored;
    } catch { /* SSR or storage error */ }
    return DEFAULT_DENSITY;
  });

  const [moduleOverrides, setModuleOverrides] = useState<Partial<Record<ModuleName, Density>>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_OVERRIDES);
      if (stored) return JSON.parse(stored);
    } catch { /* SSR or storage error */ }
    return {};
  });

  // Resolve effective density for current module
  const currentModule = detectModule(location.pathname);
  const effective = useMemo(() => {
    if (currentModule && moduleOverrides[currentModule]) {
      return moduleOverrides[currentModule];
    }
    return global;
  }, [global, moduleOverrides, currentModule]);

  // Apply data-density attribute to root
  useEffect(() => {
    document.documentElement.setAttribute("data-density", effective);
  }, [effective]);

  // Persist global
  const setGlobal = useCallback((d: Density) => {
    setGlobalState(d);
    try {
      localStorage.setItem(STORAGE_KEY_GLOBAL, d);
    } catch { /* Storage full or unavailable */ }

    // Async sync to user prefs API (fire-and-forget)
    fetch("/api/platform/user/preferences", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ density: d }),
    }).catch(() => { /* Non-critical */ });
  }, []);

  // Set per-module override (null to remove)
  const setModuleOverride = useCallback((module: ModuleName, d: Density | null) => {
    setModuleOverrides((prev) => {
      const next = { ...prev };
      if (d === null) {
        delete next[module];
      } else {
        next[module] = d;
      }

      try {
        localStorage.setItem(STORAGE_KEY_OVERRIDES, JSON.stringify(next));
      } catch { /* Storage full */ }

      return next;
    });
  }, []);

  const value = useMemo<DensityContextValue>(
    () => ({ global, moduleOverrides, effective, setGlobal, setModuleOverride }),
    [global, moduleOverrides, effective, setGlobal, setModuleOverride],
  );

  return (
    <DensityContext.Provider value={value}>
      {children}
    </DensityContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    // Fallback for components outside provider (e.g. portal components)
    return {
      global: DEFAULT_DENSITY,
      moduleOverrides: {},
      effective: DEFAULT_DENSITY,
      setGlobal: () => {},
      setModuleOverride: () => {},
    };
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Module list export (for Settings UI)
// ---------------------------------------------------------------------------

export const DENSITY_MODULES: { value: ModuleName; label: string }[] = [
  { value: "crm", label: "CRM" },
  { value: "email", label: "Email" },
  { value: "pages", label: "Pages" },
  { value: "workflows", label: "Workflows" },
  { value: "inbox", label: "Inbox" },
  { value: "ai", label: "AI" },
  { value: "analytics", label: "Analytics" },
  { value: "services", label: "Services" },
  { value: "webinars", label: "Webinars" },
  { value: "communications", label: "Communications" },
  { value: "reputation", label: "Reputation" },
  { value: "marketplace", label: "Marketplace" },
  { value: "admin", label: "Admin" },
];
